/**
 * 챗 우측 사이드 패널에 위키 페이지 미리보기를 띄우는 컴포넌트.
 *
 * - 사용자가 LLM 답변에 포함된 `[[slug]]` 위키링크를 클릭하면 호출됨.
 * - api.wikiPage(slug) 호출해 takeaway / body / outlinks 표시.
 * - 본문은 WikiMarkdown 으로 렌더 — outlinks 중 resolved=true 인 슬러그만 정상 링크.
 * - "전체 페이지 보기" 링크는 새 탭으로 열어 챗 세션 유지.
 */
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, X } from 'lucide-react';
import { useMemo } from 'react';
import { WikiMarkdown } from './WikiMarkdown';

interface Props {
  slug: string;
  onClose: () => void;
  /** [[slug]] 표시 텍스트를 페이지 제목으로 치환하기 위한 lookup. */
  slugToTitle?: Map<string, string>;
  /** 채팅에서 위키로 이동 시 돌아갈 chat session id (?from=chat&chatId=…). */
  fromChatSessionId?: string;
}

export function WikiPeekPanel({ slug, onClose, slugToTitle, fromChatSessionId }: Props) {
  const pageQuery = useQuery({
    queryKey: ['wiki', 'page', slug],
    queryFn: () => api.wikiPage(slug),
    retry: false,
  });

  const resolvedSlugs = useMemo(() => {
    const set = new Set<string>();
    if (pageQuery.data) {
      for (const o of pageQuery.data.outlinks) {
        if (o.resolved) set.add(o.slug);
      }
    }
    return set;
  }, [pageQuery.data]);

  // 외부 sync (KMS) 출처가 있으면 그 URL 로, 없으면 innermate 내부 페이지로.
  // 내부 페이지 이동 시 ?from=chat&chatId=<sessionId> 쿼리 추가 — 위키 페이지에서 "채팅으로 돌아가기" 누르면 원래 세션으로.
  const externalUrl = pageQuery.data?.externalUrl ?? null;
  const chatReturnQs = `from=chat${fromChatSessionId ? `&chatId=${encodeURIComponent(fromChatSessionId)}` : ''}`;
  const openHref = externalUrl ?? `/wikis/p/${encodeURIComponent(slug)}?${chatReturnQs}`;
  const openTitle = externalUrl ? 'PS Wiki에서 원문 확인' : '새 탭에서 전체 페이지 열기';
  const openLabel = externalUrl ? 'PS Wiki에서 원문 확인' : '새 탭에서 열기';

  return (
    <div className="flex h-full flex-col min-h-0 bg-surface">
      <header className="shrink-0 flex items-center gap-2 border-b border-border px-4 py-2.5">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-foreground-muted">위키 미리보기</div>
          <div className="truncate text-sm font-medium" title={pageQuery.data?.title ?? slug}>
            {pageQuery.data?.title ?? slug}
          </div>
        </div>
        <a
          href={openHref}
          target="_blank"
          rel="noreferrer"
          title={openTitle}
          className="h-8 inline-flex items-center gap-1.5 rounded-md px-2 text-xs text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          aria-label={openLabel}
        >
          <ExternalLink className="size-3.5" />
          <span className="hidden sm:inline">{openLabel}</span>
        </a>
        <button
          type="button"
          onClick={onClose}
          title="닫기"
          aria-label="닫기"
          className="size-8 grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        {pageQuery.isLoading ? (
          <div className="text-sm text-foreground-muted">불러오는 중…</div>
        ) : pageQuery.isError ? (
          <div className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
            페이지를 불러오지 못했습니다: {(pageQuery.error as Error).message}
          </div>
        ) : pageQuery.data ? (
          <>
            <WikiMarkdown
              takeaway={pageQuery.data.takeaway}
              body={pageQuery.data.body}
              resolvedSlugs={resolvedSlugs}
              slugToTitle={slugToTitle}
              linkSearchParam={chatReturnQs}
            />
          </>
        ) : (
          <div className="text-sm text-foreground-muted">페이지를 찾을 수 없습니다.</div>
        )}
      </div>
    </div>
  );
}
